import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";

import { Icon } from "../../components/Icon";
import { healthOf, isSectionUnavailable } from "../health";
import {
  Callout,
  ChipInput,
  Field,
  Section,
  ToggleRow,
  errText,
  relativeTime,
  useToast,
} from "../ui";
import {
  api,
  type AdminClientDetail,
  type AdminUsersPage,
  type SsoTrustMode,
} from "../../services/api";

const SESSION_TIMEOUTS = [
  { minutes: 60, label: "1 hour" },
  { minutes: 240, label: "4 hours" },
  { minutes: 480, label: "8 hours" },
  { minutes: 960, label: "16 hours" },
];

/**
 * Sign-in & SSO.
 *
 * "Verified" is derived, not stored: a successful sign-in from the tenant is
 * the only proof the trust path actually works, so the status callout reads
 * off whether anyone here has ever signed in. Allowed domains commit on Enter
 * rather than riding the section's Save — each add/remove is its own audited
 * change, and they must not leave the section sitting dirty.
 */
export function SsoSection({
  client,
  onSaved,
}: {
  client: AdminClientDetail;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const health = healthOf(client);
  const domains = Array.isArray(client.allowed_email_domains)
    ? client.allowed_email_domains
    : [];

  const { data: users } = useSWR<AdminUsersPage>(
    ["/api/admin/users", client.id, "sso"],
    () => api.admin.users.list({ company_id: client.id, page: 1, page_size: 200 }),
  );

  const lastSignIn = useMemo(() => {
    const stamps = (users?.items ?? [])
      .map((u) => u.last_login_at)
      .filter((s): s is string => Boolean(s))
      .sort();
    return stamps.length ? stamps[stamps.length - 1] : null;
  }, [users]);

  const [tenantId, setTenantId] = useState(client.m365_tenant_id ?? "");
  const [trustMode, setTrustMode] = useState<SsoTrustMode>(client.sso_trust_mode);
  const [timeout, setTimeoutMinutes] = useState(client.session_timeout_minutes);
  const [autoProvision, setAutoProvision] = useState(client.auto_provision_users);
  const [requireMfa, setRequireMfa] = useState(client.require_mfa);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(
    client.m365_tenant_id ? client.updated_at : null,
  );
  const [domainBusy, setDomainBusy] = useState(false);

  useEffect(() => {
    setTenantId(client.m365_tenant_id ?? "");
    setTrustMode(client.sso_trust_mode);
    setTimeoutMinutes(client.session_timeout_minutes);
    setAutoProvision(client.auto_provision_users);
    setRequireMfa(client.require_mfa);
  }, [client]);

  const dirty =
    tenantId !== (client.m365_tenant_id ?? "") ||
    trustMode !== client.sso_trust_mode ||
    timeout !== client.session_timeout_minutes ||
    autoProvision !== client.auto_provision_users ||
    requireMfa !== client.require_mfa;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.admin.clients.update(client.id, {
        m365_tenant_id: tenantId.trim() || null,
        sso_trust_mode: trustMode,
        session_timeout_minutes: timeout,
        auto_provision_users: autoProvision,
        require_mfa: requireMfa,
      });
      await onSaved();
      setSavedAt(new Date().toISOString());
      toast("Saved Sign-in & SSO");
    } catch (e) {
      setError(errText(e, "Save failed."));
    } finally {
      setSaving(false);
    }
  }

  async function setDomains(next: string[], message: string) {
    setDomainBusy(true);
    try {
      await api.admin.clients.update(client.id, { allowed_email_domains: next });
      await onSaved();
      toast(message);
    } catch (e) {
      toast(errText(e, "Could not update the domain list"), "warn");
    } finally {
      setDomainBusy(false);
    }
  }

  return (
    <Section
      title="Sign-in & SSO"
      short="sign-in"
      desc="Staff sign in with their Microsoft or Google work account. No portal passwords."
      dirty={dirty}
      saving={saving}
      error={error}
      savedLabel={relativeTime(savedAt)}
      onSave={save}
      onDiscard={() => {
        setTenantId(client.m365_tenant_id ?? "");
        setTrustMode(client.sso_trust_mode);
        setTimeoutMinutes(client.session_timeout_minutes);
        setAutoProvision(client.auto_provision_users);
        setRequireMfa(client.require_mfa);
        setError(null);
      }}
    >
      <div style={{ marginBottom: 18 }}>
        {isSectionUnavailable(health, "sso") || !client.setup_health ? (
          <Callout kind="warn">
            <strong>Sign-in status could not be read.</strong> The domain list below is
            still editable.
          </Callout>
        ) : health.sso === "ok" ? (
          <Callout kind="ok">
            <strong>Sign-in verified.</strong> Someone at {client.name} last signed in{" "}
            {relativeTime(lastSignIn) ?? "recently"}, so the trust path works.
          </Callout>
        ) : domains.length === 0 ? (
          <Callout kind="warn">
            <strong>Nobody at {client.name} can sign in yet.</strong> Sign-in is granted by
            email domain, and this list is empty, which is fail-closed for Microsoft and
            Google alike. Add their domain below.
          </Callout>
        ) : (
          <Callout kind="warn">
            <strong>Set up, but unproven.</strong> Anyone at {domains.join(" or ")} can sign
            in; nobody has yet. It verifies itself on the first successful sign-in.
          </Callout>
        )}
      </div>

      <div className="ad-grid">
        <Field
          label="Allowed email domains"
          hint="This is what grants sign-in"
          span
          note="Saved the moment you add or remove one, and audited on its own. Anyone with a work account on these domains can sign in, by either provider. They also gate which addresses receive portal email."
        >
          <ChipInput
            values={domains}
            disabled={domainBusy}
            placeholder="Add a domain and press Enter"
            onAdd={(v) => void setDomains([...domains, v], "Domain added")}
            onRemove={(v) =>
              void setDomains(
                domains.filter((d) => d !== v),
                "Domain removed",
              )
            }
          />
        </Field>
        <Field
          label="Session timeout"
          note="Stored now; the idle watchdog that consumes it ships separately."
        >
          <select
            value={String(timeout)}
            onChange={(e) => setTimeoutMinutes(parseInt(e.target.value, 10))}
          >
            {!SESSION_TIMEOUTS.some((o) => o.minutes === timeout) && (
              <option value={String(timeout)}>{timeout} minutes</option>
            )}
            {SESSION_TIMEOUTS.map((o) => (
              <option key={o.minutes} value={String(o.minutes)}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div style={{ marginTop: 14 }}>
        <ToggleRow
          label="Create accounts on first sign-in"
          sub="Anyone from an allowed domain gets a portal user automatically. Off means you add every person by hand, and nobody new can provision."
          checked={autoProvision}
          onChange={setAutoProvision}
        />
        <ToggleRow
          label="Require MFA"
          sub="Sign-ins without an MFA claim are refused. Stored now; enforcement is held behind a global flag until Conditional Access is confirmed to emit the claim."
          checked={requireMfa}
          onChange={setRequireMfa}
        />
      </div>

      <div className="ad-eyebrow" style={{ margin: "24px 0 10px" }}>
        Microsoft 365
      </div>
      <div className="ad-grid">
        <Field
          label="Microsoft 365 tenant ID"
          hint="Entra ID › Overview › Tenant ID"
          note="Not a sign-in gate — the domains above are. This identifies the tenant for the daily Entra mailbox sweep that Status Alerts email."
        >
          <input
            value={tenantId}
            maxLength={100}
            placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
            onChange={(e) => setTenantId(e.target.value)}
          />
        </Field>
        <Field label="Trust mode">
          <select
            value={trustMode}
            onChange={(e) => setTrustMode(e.target.value as SsoTrustMode)}
          >
            <option value="b2b_guest">B2B guest (recommended)</option>
            <option value="federated">Federated (direct multi-tenant)</option>
          </select>
        </Field>
      </div>

      {client.m365_tenant_id && (
        <EntraMailboxSync
          companyId={client.id}
          tenantEdited={tenantId.trim() !== client.m365_tenant_id}
        />
      )}

      <div className="ad-eyebrow" style={{ margin: "24px 0 10px" }}>
        Google Workspace
      </div>
      <Callout>
        Nothing to configure per customer. Google sign-in runs through one
        iQuest-owned OAuth client and is switched on for the whole portal, so a
        Google Workspace customer needs only their domain in the list above.
      </Callout>
    </Section>
  );
}

/**
 * Re-sweep this customer's licensed Entra mailboxes from CIPP now.
 *
 * Status Alerts also reach licensed staff in the tenant who have no portal
 * account, from a mirror refreshed daily at 05:00 AWST. That is right for the
 * steady state and wrong for exactly one moment: a customer onboarded, or a
 * tenant id entered, after this morning's sweep has no Entra audience at all
 * until tomorrow. This is the escape hatch for that, and for re-running a
 * tenant whose last sweep errored.
 *
 * Microsoft-only by nature: CIPP is Microsoft-CSP-only and can never see a
 * Google Workspace tenant, which is why this lives under the Microsoft
 * heading and only appears once a tenant is SAVED.
 */
function EntraMailboxSync({
  companyId,
  tenantEdited,
}: {
  companyId: string;
  tenantEdited: boolean;
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ tone: "ok" | "warn"; text: string } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setBusy(true);
    setResult(null);
    setError(null);
    try {
      const r = await api.admin.clients.cippMailboxes.refresh(companyId);
      if (r.companies === 0) {
        setResult({
          tone: "warn",
          text: "No linked M365 tenant saved yet — save this section, then sync.",
        });
      } else if (r.failed > 0) {
        // The sweep prunes only after a successful pull, so a failure leaves
        // the previous roster in place. Say that: a stale audience beats an
        // empty one, which would look exactly like "nobody to email".
        setResult({
          tone: "warn",
          text: "Sync failed for this tenant. The previous mailbox list is still in use, so alerts still have an audience.",
        });
      } else {
        setResult({
          tone: "ok",
          text: `${r.mailboxes.toLocaleString()} licensed mailbox${
            r.mailboxes === 1 ? "" : "es"
          } synced.`,
        });
      }
    } catch (e) {
      setError(errText(e, "Sync failed."));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        marginTop: 18,
        paddingTop: 16,
        borderTop: "1px solid var(--iq-border)",
      }}
    >
      <div style={{ fontSize: 13.5, fontWeight: 500 }}>Entra mailbox sync</div>
      <div className="ad-note" style={{ marginTop: 4, marginBottom: 10, maxWidth: "60ch" }}>
        Status alerts also reach licensed staff in this tenant who have no portal
        account. That list refreshes by itself every morning at 5am AWST — sync now
        only if you have just linked the tenant and need to send today.
      </div>
      {tenantEdited && (
        <div style={{ marginBottom: 10 }}>
          <Callout kind="warn">
            You have an unsaved tenant ID. The sweep reads the saved value, so save
            this section first or it will sync the old tenant.
          </Callout>
        </div>
      )}
      <button type="button" className="btn btn-ghost btn-sm" onClick={() => void run()} disabled={busy}>
        <Icon name="refresh" size={15} />{" "}
        {busy ? "Syncing… this can take up to 90 seconds" : "Sync mailboxes now"}
      </button>
      {result && (
        <div
          className={result.tone === "ok" ? "form-success" : "ad-note"}
          style={{ marginTop: 10 }}
        >
          {result.text}
        </div>
      )}
      {error && (
        <div className="form-error" style={{ marginTop: 10 }}>
          {error}
        </div>
      )}
    </div>
  );
}
